import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Typography,
  Button,
  Descriptions,
  Row,
  Col,
  Space,
  Spin,
  Tabs,
  Tag,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Reservation, ReservationStatus, ReservationType } from '@/types/residents/reservation';
import { getReservationById, updateReservation, deleteReservation } from '@/api/residents';
import { useAuth } from '@/contexts/AuthContext';
import ActivityLogList from '@/components/common/ActivityLogList';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import ReservationModal from '../../components/features/residents/ReservationModal';
import { ResidentTab, EmployeeTab, VendorTab } from '@/components/tab';

const { Title, Text } = Typography;


const ReservationDetailsPage: React.FC = () => {
  const { t } = useTranslation(['reservations', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();
  
  const state = location.state as { reservation: Reservation } | null;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Query - CRITICAL: Only fetch if no state data is available
  const { data: reservationData, isLoading } = useQuery({
    queryKey: ['reservation', id],
    queryFn: () => getReservationById(id!, firestoreUser ?? undefined),
    enabled: !!id && !state?.reservation && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  // Use state data if available, otherwise use fetched data
  const displayReservation = state?.reservation || reservationData;

  // Mutations
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Reservation> }) =>
      updateReservation(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('reservations:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['reservation', id] });
      setIsEditModalOpen(false);
    },
    onError: () => {
      message.error(t('reservations:updateError'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteReservation(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('reservations:deleteSuccess'));
      navigate('/residents/reservations');
    },
    onError: () => {
      message.error(t('reservations:deleteError'));
    },
  });

  // Loading state - only show if no state data is available
  if (isLoading && !state?.reservation) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayReservation) {
    return <Card>{t('reservations:notFound')}</Card>;
  }

  const getStatusColor = (status: ReservationStatus) => {
    const colors: Record<ReservationStatus, string> = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      cancelled: 'default',
      completed: 'processing',
    };
    return colors[status];
  };

  const getTypeColor = (type: ReservationType) => {
    const colors: Record<ReservationType, string> = {
      personal: 'blue',
      community: 'green',
      commercial: 'purple',
    };
    return colors[type];
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleEditSuccess = (data: Omit<Reservation, 'id'>) => {
    updateMutation.mutate({ id: displayReservation.id, data });
  };

  const handleDeleteConfirm = () => {
    deleteMutation.mutate(displayReservation.id);
  };

  // Prepare tab items
  const tabItems = [
    {
      key: 'details',
      label: t('reservations:details'),
      children: (
        <Card>
          <Descriptions bordered column={2}>
            <Descriptions.Item label={t('reservations:amenity')} span={2}>
              <Text strong>{displayReservation.amenity.name}</Text>
            </Descriptions.Item>
            
            <Descriptions.Item label={t('reservations:type')}>
              <Tag color={getTypeColor(displayReservation.type)}>
                {t(`reservations:types.${displayReservation.type}`)}
              </Tag>
            </Descriptions.Item>
            
            <Descriptions.Item label={t('reservations:status')}>
              <Tag color={getStatusColor(displayReservation.status)}>
                {t(`reservations:statuses.${displayReservation.status}`)}
              </Tag>
            </Descriptions.Item>
            
            <Descriptions.Item label={t('reservations:startTime')}>
              {displayReservation.startTime ? dayjs(displayReservation.startTime).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label={t('reservations:endTime')}>
              {displayReservation.endTime ? dayjs(displayReservation.endTime).format('YYYY-MM-DD HH:mm') : '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label={t('reservations:attendees')}>
              {displayReservation.attendees || '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label={t('reservations:purpose')} span={2}>
              {displayReservation.purpose}
            </Descriptions.Item>
            
            {displayReservation.resident && (
              <Descriptions.Item label={t('reservations:resident')}>
                {displayReservation.resident.name}
              </Descriptions.Item>
            )}
            
            {displayReservation.employee && (
              <Descriptions.Item label={t('reservations:employee')}>
                {displayReservation.employee.name}
              </Descriptions.Item>
            )}
            
            {displayReservation.vendor && (
              <Descriptions.Item label={t('reservations:vendor')}>
                {displayReservation.vendor.name}
              </Descriptions.Item>
            )}
            
            {displayReservation.receivable && (
              <Descriptions.Item label={t('reservations:receivable')}>
                {displayReservation.receivable.name}
              </Descriptions.Item>
            )}
            
            {displayReservation.notes && (
              <Descriptions.Item label={t('reservations:notes')} span={2}>
                {displayReservation.notes}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      ),
    },
  ];

  // Add resident tab if available
  if (displayReservation.resident) {
    tabItems.push({
      key: 'resident',
      label: t('reservations:resident'),
      children: <ResidentTab residentId={displayReservation.resident.id} />,
    });
  }

  // Add employee tab if available
  if (displayReservation.employee) {
    tabItems.push({
      key: 'employee',
      label: t('reservations:employee'),
      children: <EmployeeTab employeeId={displayReservation.employee.id} />,
    });
  }

  // Add vendor tab if available
  if (displayReservation.vendor) {
    tabItems.push({
      key: 'vendor',
      label: t('reservations:vendor'),
      children: <VendorTab vendorId={displayReservation.vendor.id} />,
    });
  }

  // Add activity log tab
  tabItems.push({
    key: 'activity',
    label: t('reservations:activityLog'),
    children: (
      <Card>
        <ActivityLogList activityRecords={displayReservation.activityRecords || []} />
      </Card>
    ),
  });

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              {t('common:back')}
            </Button>
            <Title level={3}>
              {t('reservations:reservation')} #{displayReservation.id}
            </Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Content */}
      <Tabs defaultActiveKey="details" items={tabItems} />

      {/* Modals */}
      <ReservationModal
        open={isEditModalOpen}
        reservation={displayReservation}
        onSuccess={handleEditSuccess}
        onCancel={() => setIsEditModalOpen(false)}
      />

      <DeleteConfirmationModal
        visible={isDeleteModalOpen}
        title={t('reservations:deleteConfirmTitle')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </div>
  );
};

export default ReservationDetailsPage; 